import { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';
import { anyAccess, can as canOn, normalize } from '../pages/Dashboard/Admin/adminPermissions';

/**
 * Current user's admin role and per-card permissions. Both come from
 * GET /bff/profile (the DB user's role column plus the effective permission
 * map the server computes), so a change takes effect on the next load without
 * re-login.
 *
 * Roles (migration 018): 'admin' and 'superadmin'. Since 10-07 (migration 026)
 * an admin's access is per card, so "is an admin at all" means "has at least
 * one card" - hence anyAccess rather than a role equality check. 'superadmin'
 * is full access everywhere.
 */

const ADMIN_ROLES = ['admin', 'superadmin'];

export function useAdminRole() {
  const [state, setState] = useState(undefined); // undefined = still loading
  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE_URL}/bff/profile`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        const p = d?.profile;
        setState({ role: p?.role || null, permissions: normalize(p?.permissions) });
      })
      .catch(() => { if (alive) setState({ role: null, permissions: normalize(null) }); });
    return () => { alive = false; };
  }, []);

  const loading = state === undefined;
  const permissions = state?.permissions || null;
  const can = useCallback(
    (tab, card, level) => (permissions ? canOn(permissions, tab, card, level) : false),
    [permissions]
  );

  return {
    role: state?.role,
    permissions,
    can,
    loading,
    isAdmin: loading ? null : anyAccess(permissions),
    isElevated: loading ? null : state.role === 'superadmin',
  };
}

/** Returns null while loading, then true/false. */
export function useIsAdmin() {
  return useAdminRole().isAdmin;
}

export { ADMIN_ROLES };
